import { Component } from '@theme/component';
import { getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * @typedef {Object} BuyBarRefs
 * @property {HTMLAnchorElement} link - The bar's link to the product page
 */

/**
 * A custom element that shows a bar linking to a product once the page's own link to that product is scrolled past.
 * Without such a link on the page, the bar shows once the section after it is scrolled past.
 *
 * @extends {Component<BuyBarRefs>}
 */
class BuyBarComponent extends Component {
  requiredRefs = ['link'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();

    if (Shopify.designMode) {
      this.dataset.visible = 'true';
      return;
    }

    this.#observe();
    scrollContainerMediaQuery.addEventListener('change', this.#observe);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    scrollContainerMediaQuery.removeEventListener('change', this.#observe);
  }

  /**
   * Watches the trigger against the current scroll container, which changes at the squeeze breakpoint.
   */
  #observe = () => {
    this.#observer?.disconnect();

    const trigger = this.#findTrigger();
    if (!trigger) return;

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;

        const top = entry.rootBounds?.top ?? 0;
        const isScrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom <= top;
        this.dataset.visible = String(isScrolledPast);
      },
      { root: getIntersectionRoot() }
    );
    this.#observer.observe(trigger);
  };

  /**
   * @returns {Element | null | undefined} The first link on the page to the bar's product, else the next section
   */
  #findTrigger() {
    const { pathname } = new URL(this.refs.link.href);
    const links = document.querySelectorAll('main a[href]');

    for (const link of links) {
      if (this.contains(link) || !(link instanceof HTMLAnchorElement)) continue;
      if (new URL(link.href).pathname === pathname) return link;
    }

    return this.closest('.shopify-section')?.nextElementSibling;
  }
}

if (!customElements.get('buy-bar-component')) {
  customElements.define('buy-bar-component', BuyBarComponent);
}
